import { clients } from '@/lib/https';
import {
  GetPreSignedUrlForUploadPayload,
  GetPreSignedUrlForUploadResponse,
  GetFileResponse,
} from '../types/storage.types';

/**
 * Storage service — wraps the Blocks DMS endpoints.
 *
 * Both endpoints live under /data/v4 on the project-scoped host
 * (https://blocksapi.<cookieDomain>). The legacy /storage/v1/... paths
 * 404 on this host.
 *
 * `getPreSignedUrlForUpload` returns { uploadUrl, fileId }. The client then
 * PUTs the binary to `uploadUrl` directly (the storage provider authorizes
 * that URL). `getFileUrl` resolves a fileId → { url } for display.
 */

export const getPreSignedUrlForUpload = async (
  payload: GetPreSignedUrlForUploadPayload
): Promise<GetPreSignedUrlForUploadResponse> => {
  const params = new URLSearchParams({
    Name: payload.name,
    ProjectKey: payload.projectKey,
  });
  if (payload.itemId) params.append('ItemId', payload.itemId);
  if (payload.metaData) params.append('MetaData', payload.metaData);
  if (payload.tags) params.append('Tags', payload.tags);
  if (payload.accessModifier) params.append('AccessModifier', payload.accessModifier);
  if (payload.configurationName) params.append('ConfigurationName', payload.configurationName);
  if (payload.parentDirectoryId !== undefined) {
    params.append('ParentDirectoryId', payload.parentDirectoryId);
  }
  return clients.post<GetPreSignedUrlForUploadResponse>(
    `/data/v4/Files/GetPreSignedUrlForUpload?${params.toString()}`,
    ''
  );
};

/**
 * Resolve a DMS fileId to a publicly-readable URL.
 * GET /data/v4/Files/GetFile?FileId=&ConfigurationName= returns { url }.
 */
export const getFileUrl = async (
  fileId: string,
  configurationName = 'Default'
): Promise<GetFileResponse> => {
  const params = new URLSearchParams({
    FileId: fileId,
    ConfigurationName: configurationName,
  });
  return clients.get<GetFileResponse>(`/data/v4/Files/GetFile?${params.toString()}`);
};